'use client';
import { useEffect, useRef, useState } from 'react';
// -------------- assets -------------------- //
import ArrowRight from '@/assets/icons/arrow-right-new.svg';
import Next from '@/assets/icons/next.svg';
import Prev from '@/assets/icons/prev.svg';
// -------------- styles -------------------- //
import { chunkArray, getTopTag, returnCurrentRankPage } from '../../best-seller/utils';
import styles from './BestSellerMainCard.module.scss';
// -------------- components -------------------- //
import JDIProductCard from '@/components/productCard/components/type5';
import { ProductState } from '@/components/productCard/index.interface';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/32521';

interface BestSellerMainCardPropTypes {
  sequenceId: number;
  data: any;
  datas: any;
  item: any;
  currencyCode: string;
  languageCode: string;
  index: number;
}
const BestSellerMainCard = ({
  sequenceId,
  item,
  data,
  currencyCode,
  languageCode,
  index,
  datas,
}: BestSellerMainCardPropTypes) => {
  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const swiperContainerRef = useRef(null);
  const swiperInstanceRef = useRef<any>(null);
  const [swiperReady, setSwiperReady] = useState(false);
  // -------------- ue -------------------- //
  useEffect(() => {
    // Only run on client-side
    if (typeof window === 'undefined') return;
    const initializeSwiper = async () => {
      // Dynamically import Swiper and its modules
      const { default: Swiper } = await import('swiper');
      const { Navigation } = await import('swiper/modules');

      if (swiperContainerRef.current && !swiperInstanceRef.current) {
        swiperInstanceRef.current = new Swiper(swiperContainerRef.current, {
          loop: false,
          slidesPerView: 1,
          modules: [Navigation],
          navigation: {
            prevEl: prevButtonRef.current,
            nextEl: nextButtonRef.current,
          },
        });
        setSwiperReady(true);
      }
    };

    initializeSwiper();

    return () => {
      if (swiperInstanceRef.current) {
        swiperInstanceRef.current.destroy();
        swiperInstanceRef.current = null;
      }
    };
  }, []);

  const groupArray = data && chunkArray(data, 4);

  // -------------- return -------------------- //

  return (
    <div
      className={styles.container}
      exptag={'exp|' + EXPOSURE_RECORD.Activity_BestSellersHome_RankCard_Expo}
      data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
      data-exptag-json-param={JSON.stringify({
        rankId: item?.sequenceId || '-100',
        rankName: item?.categoryTabName || '-100',
        index: index || -100,
        type: `${item?.type}` || '-100',
        cateLevel: item?.level || '-100',
        skuid: (item && item?.productBestSellerRanks.map((item: any) => `${item.skuId}`)) || [
          '-100',
        ],
        tabName: item?.categoryTabName || '-100',
        scoreNum: item?.viewedStr || '-100',
      })}
    >
      <div className={styles.containerHeader}>
        <h2>{item?.categoryTabName}</h2>
        <a
          exptag={'exp|' + EXPOSURE_RECORD.Activity_BestSellersHome_LookMoreExpo}
          data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
          data-exptag-json-param={JSON.stringify({
            type: `${item?.type}` || '-100',
            rankId: item?.sequenceId || '-100',
            rankName: item?.categoryTabName || '-100',
            scoreNum: item?.viewedStr || '-100',
            cateLevel: item?.level || '-100',
            tabName: item?.categoryTabName || '-100',
            skuid: (item && item.productBestSellerRanks.map((item: any) => `${item.skuId}`)) || [
              '-100',
            ],
          })}
          href={returnCurrentRankPage(sequenceId, item?.categoryTabName)}
          onClick={() =>
            sendClickTrack.Activity_BestSellersHome_LookMore({
              rankId: item?.sequenceId || '-100',
              rankName: item?.categoryTabName || '-100',
              rankPos: index || -100,
              type: `${item?.type}` || '-100',
              skuid: (item && item.productBestSellerRanks.map((item: any) => `${item.skuId}`)) || [
                '-100',
              ],
              cateLevel: item?.level || '-100',
              tabName: item?.categoryTabName || '-100',
              scoreNum: item?.viewedStr || '-100',
              index: index || -100,
            })
          }
        >
          <span className={styles.saleQtyText}>{item?.viewedStr}</span>
          <ArrowRight />
        </a>
      </div>
      <div className={`${styles.containerContent} swiper mySwiper`} ref={swiperContainerRef}>
        <div className={`${styles.wrapper} swiper-wrapper`}>
          {groupArray &&
            groupArray.map((grouped: any, index: number) => {
              return (
                <div key={index} className={`swiper-slide ${styles.innerContainer}`}>
                  {grouped &&
                    grouped.map((product: any, idx: number) => {
                      return (
                        <div className={`${styles.card}`} key={idx}>
                          <JDIProductCard
                            key={index}
                            productData={
                              getProductData(
                                product,
                                index,
                                item,
                                currencyCode,
                                languageCode,
                              ) as any
                            }
                          />
                        </div>
                      );
                    })}
                </div>
              );
            })}
        </div>

        {swiperReady && (
          <>
            <button
              ref={prevButtonRef}
              className={`${styles.prev} swiper-button-prev"`}
              onClick={() => {
                swiperInstanceRef.current.slidePrev();
              }}
            >
              <Prev />
            </button>
            <button
              ref={nextButtonRef}
              className={`${styles.next} swiper-button-next`}
              onClick={() => {
                swiperInstanceRef.current.slideNext();
              }}
            >
              <Next />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BestSellerMainCard;

const getProductData = (
  product: any,
  index: number,
  item: any,
  currencyCode: string,
  languageCode: string,
) => {
  const trackParam = {
    skuid: product?.skuId || '-100',
    firprice: parseFloat(product?.realPrice) || -100,
    firpricetype: '1',
    secprice: parseFloat(product?.originalPriceV2) || -100,
    secpricetype: '1',
    rankId: item?.sequenceId || '-100',
    rankName: item?.categoryTabName || '-100',
    rankNum: product?.rank || '-100',
    rankPos: product?.rank || '-100',
    type: item?.type || '-100',
    cateLevel: item?.level || '-100',
    tabName: item?.categoryTabName || '-100',
  } as any;

  const rank = product?.rank as number;
  return {
    type: 5,
    viewedTip: product?.viewedStr,
    skuId: product?.skuId,
    skuName: product?.skuName,
    skuImg: product?.skuImage,
    shouldShowCart: true,
    shouldShowPrice: true,
    shouldShowMarketing: true,
    brandName: product?.brandName,
    realPrice: product?.realPrice,
    originalPrice: product?.originalPriceV2,
    originalPriceTips: product?.originalPriceDesc,
    currency: currencyCode,
    languageCode,
    productState:
      product.wareInStock === '0'
        ? ProductState.OUT_OF_STICK
        : product?.stockStatus === 1
          ? ProductState.IN_STOCK
          : ProductState.OUT_OF_STICK,
    imgTips: {
      position: 'topLeft',
      tipDom: getTopTag({ index: rank - 1, width: 30, height: 39 }),
    },
    promotions: product?.labels,
    // ---- styles ------ //
    containerStyle: {
      display: 'flex',
      flexDirection: 'column',
    },
    trackingPointParams: {
      productExpConfig: {
        event_id: EXPOSURE_RECORD.Activity_BestSellersHome_ProductExpo,
        config: {
          useClick: false,
          stay_duration: 0.3,
          repeated: false,
          area_rate: 0.5,
        },
        json_param: trackParam,
      },
      cartExpConfig: {
        event_id: EXPOSURE_RECORD.Activity_BestSellersHome_Cart_Ep,
        config: {
          useClick: false,
          stay_duration: 0,
          repeated: false,
          area_rate: 0.01,
        },
        json_param: {
          rank_no: product?.rank || '-100',
          rankid: item?.sequenceId || '-100',
          rank_name: item?.categoryTabName || '-100',
          type: item?.type || '-100',
          cateLevel: item?.level || '-100',
        },
      },

      wishClickPoint: () => {
        sendClickTrack.Activity_BestSellers_Favorite_Expo({
          rankId: item?.sequenceId || '-100',
          rankName: item?.categoryTabName || '-100',
          type: item?.type || '-100',
          scoreNum: item?.viewedStr || '-100',
          cateLevel: item?.level || '-100',
          skuId: product?.skuId || '-100',
          rankNum: product?.rank || '-100',
        });
      },

      productClickPoint: () =>
        sendClickTrack.Activity_BestSellersHome_Product({
          skuid: product?.skuId || '-100',
          firprice: parseFloat(product?.realPrice) || -100,
          firpricetype: '1',
          secprice: parseFloat(product?.originalPriceV2) || -100,
          secpricetype: '1',
          rankId: item?.sequenceId || '-100',
          rankName: item?.categoryTabName || '-100',
          rankNum: product?.rank || '-100',
          type: item?.type || '-100',
          cateLevel: item?.level || '-100',
          tabName: item?.categoryTabName || '-100',
          scoreNum: item?.viewedStr || '-100',
        }),
      cartClickPoint: () =>
        sendClickTrack.Activity_BestSellersHome_Cart({
          skuid: product?.skuId || '-100',
          firprice: parseFloat(product?.realPrice) || -100,
          firpricetype: '1',
          secprice: parseFloat(product?.originalPriceV2) || -100,
          secpricetype: '1',
          rankId: item?.sequenceId || '-100',
          rankName: item?.categoryTabName || '-100',
          rankNum: product?.rank || '-100',
          type: item?.type || '-100',
          cateLevel: item?.level || '-100',
          tabName: item?.categoryTabName || '-100',
        }),
    },
  };
};
